import { IconAlertTriangle, IconArrowsDiagonal, IconExclamationMark } from '@tabler/icons-react';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '@hooks/useDispatch';
import { setFocusedCell, selectFocusedCell } from '@store/Spreadsheet/focusSlice';
import { selectEditingCell } from '@store/Spreadsheet/editingSlice';
import { addAdditionalRange, setPrimaryRange } from '@store/Spreadsheet/selectionSlice';
import {
  CellStatusType,
  cellChangesKey,
  selectCellChangesMap
} from '@store/Spreadsheet/cellChangesSlice';
import { Column, Row } from '../../../../types/Spreadsheet';
import classes from './DataRow.module.css';
import { useAppSelector } from '@hooks/useAppSelector';
import {
  selectAllRanges,
  selectAnchorCell,
  selectCutCellKeySet,
  selectRowStatus,
  selectVisibleColumns,
  selectVisualRows
} from '@store/Spreadsheet/selectors';
import { getCellKey } from '@utils/grid';
import { Button, CloseButton, Group, Modal, Stack, Text, Tooltip } from '@mantine/core';
import Cell from '../Cell/Cell';
import { computeCutEdges, CutEdges } from '../Cell/cutEdges';
import { useSpreadsheetConfig } from '../SpreadsheetConfigContext';
import { componentsTestIds } from '@components/testids';

type CellFacts = {
  /**
   * TV1-2358 — the character that opened this edit, when it was opened by typing
   * over the cell. Computed here with the other per-cell facts so `Cell` keeps
   * its zero-subscription property.
   */
  editingInitialValue?: string;
  isFocused: boolean;
  isEditing: boolean;
  isSelected: boolean;
  status: CellStatusType;
};

type DataRowProps = {
  row: Row;
  index: number;
  columns: Column[];
  isRowSelected: boolean;
  // Virtualizer-driven positioning. Provided by GridBody.
  virtualItemIndex: number;
  start: number;
  measureRef: ( node: Element | null ) => void;
};

const DataRowComponent: React.FC<DataRowProps> = ({ row, index, columns, isRowSelected, virtualItemIndex, start, measureRef }) => {
  const dispatch = useAppDispatch();
  const visualRows = useAppSelector( selectVisualRows );
  const visibleColumns = useAppSelector( selectVisibleColumns );
  // Back-compat anchor cell selector — returns `{ rowId, columnId } | null`
  // derived from primary.anchorRow/anchorCol via visualRows + visibleColumns.
  const anchorCell = useAppSelector( selectAnchorCell );
  const rowStatus = useAppSelector(( state ) => selectRowStatus( state, row.id ));
  // Optional per-row streaming status (TV1-1877). `rowStatusFn` is undefined for
  // every consumer that doesn't pass the `rowStatus` prop (e.g. inventory bulk
  // edit) — in that case `streamStatus` stays undefined and nothing below
  // changes the render. The lookup is a plain function call (no subscription);
  // consumers re-feed a new function reference to push fresh results.
  const { rowStatusFn } = useSpreadsheetConfig();
  const streamStatus = rowStatusFn ? rowStatusFn( row.id ) : undefined;
  const { t } = useTranslation( 'grid' );
  // Per-row error modal (TV1-1877): a failed row swaps its expand icon for a red
  // exclamation that opens this modal with the channel error reason.
  const [ errorModalOpen, setErrorModalOpen ] = useState( false );
  // Cut-edge derivation (TV1-1690 / B-5a). One Set subscription per visible
  // row instead of every cell subscribing to a whole-grid border map.
  const cutCellKeySet = useAppSelector( selectCutCellKeySet );
  // Per-cell facts lifted from inside Cell (TV1-1690 / B-5c). Each of these
  // subscriptions runs ONCE per visible row instead of once per visible cell.
  // At 5k visible cells (~30 rows x 50 cols windowed) this drops ~6000 cell
  // subscriptions down to ~120 row-level ones.
  const focusedCell = useAppSelector( selectFocusedCell );
  const editingCell = useAppSelector( selectEditingCell );
  const ranges = useAppSelector( selectAllRanges );
  const cellChangesMap = useAppSelector( selectCellChangesMap );

  // Per-cell marching-ants edges for THIS row. When no cut is active we
  // short-circuit to `null` — every Cell receives `cutEdges={null}` and the
  // four overlay divs are skipped. When a cut IS active we compute four
  // neighbor keys per visible column and look each up in `cutCellKeySet`.
  const cutEdgesByColumnId = useMemo<Record<string, CutEdges | null> | null>(() => {
    if ( cutCellKeySet.size === 0 ) return null;

    const out: Record<string, CutEdges | null> = {};
    const rowAbove = index > 0 ? visualRows[index - 1] : null;
    const rowBelow = index < visualRows.length - 1 ? visualRows[index + 1] : null;

    for ( let cIdx = 0; cIdx < visibleColumns.length; cIdx++ ) {
      const col = visibleColumns[cIdx];
      const cellKey = getCellKey({ rowId: row.id, columnId: col.id });
      const rowAboveKey = rowAbove ? getCellKey({ rowId: rowAbove.id, columnId: col.id }) : null;
      const rowBelowKey = rowBelow ? getCellKey({ rowId: rowBelow.id, columnId: col.id }) : null;
      const colLeftKey = cIdx > 0 ? getCellKey({ rowId: row.id, columnId: visibleColumns[cIdx - 1].id }) : null;
      const colRightKey = cIdx < visibleColumns.length - 1
        ? getCellKey({ rowId: row.id, columnId: visibleColumns[cIdx + 1].id })
        : null;
      out[col.id] = computeCutEdges( cellKey, cutCellKeySet, rowAboveKey, rowBelowKey, colLeftKey, colRightKey );
    }
    return out;
  }, [ cutCellKeySet, visualRows, visibleColumns, row.id, index ]);

  // Per-column facts for this row (TV1-1690 / B-5c). Computed once per render
  // from the row-level subscriptions above; each Cell reads from this record
  // via `col.id` and the React.memo comparator on Cell short-circuits when
  // the four primitives are unchanged.
  //
  // Selection check uses the same range model selectVisualRows/visibleColumns
  // indexing as selectors.ts:rangeBounds — keep them in sync if the model
  // changes.
  //
  // Status uses the cellChanges slice's own `///`-separated key encoding
  // (cellChangesKey) and NOT `getCellKey`. See concerns.md "selectRowStatus
  // separator mismatch" — that pre-existing inconsistency is a separate
  // ticket; we preserve current behavior here.
  const cellFactsByColumnId = useMemo<Record<string, CellFacts>>(() => {
    const out: Record<string, CellFacts> = {};
    const focusedHere = focusedCell?.rowId === row.id;
    const editingHere = editingCell?.rowId === row.id;
    for ( let cIdx = 0; cIdx < visibleColumns.length; cIdx++ ) {
      const col = visibleColumns[cIdx];
      const isFocused = !!focusedHere && focusedCell?.columnId === col.id;
      const isEditing = !!editingHere && editingCell?.columnId === col.id;
      // TV1-2358: only set when the edit was opened by typing over the cell, so
      // the editor can seed its buffer with that character.
      const editingInitialValue = isEditing ? editingCell?.initialValue : undefined;

      let isSelected = false;
      for ( const r of ranges ) {
        const minRow = Math.min( r.anchorRow, r.focusRow );
        const maxRow = Math.max( r.anchorRow, r.focusRow );
        const minCol = Math.min( r.anchorCol, r.focusCol );
        const maxCol = Math.max( r.anchorCol, r.focusCol );
        if ( index >= minRow && index <= maxRow && cIdx >= minCol && cIdx <= maxCol ) {
          isSelected = true;
          break;
        }
      }

      const key = cellChangesKey( row.id, col.id );
      const status: CellStatusType = cellChangesMap[key]?.status ?? 'none';

      out[col.id] = { isFocused, isEditing, isSelected, status, editingInitialValue };
    }
    return out;
  }, [ focusedCell, editingCell, ranges, cellChangesMap, visibleColumns, row.id, index ]);

  // Row-number click translates to a row-spanning range selection. Index
  // math is over `visualRows` (post filter/sort/group) and the visible
  // columns array — the same indexing the selection slice expects.
  const handleRowNumberClick = ( e: React.MouseEvent<HTMLDivElement> ) => {
    const visibleColumns = columns.filter(( c ) => !c.hidden );
    if ( !visibleColumns.length ) return;

    const rowIdx = visualRows.findIndex(( r ) => r.id === row.id );
    if ( rowIdx < 0 ) return;
    const lastColIdx = visibleColumns.length - 1;

    const firstCell = { rowId: row.id, columnId: visibleColumns[0].id };

    if ( e.shiftKey ) {
      // Shift+click row number: extend a row-spanning selection from the
      // anchor row to the clicked row (full visible-column span).
      const anchorRowIdx = anchorCell ? visualRows.findIndex(( r ) => r.id === anchorCell.rowId ) : -1;
      if ( anchorRowIdx < 0 ) {
        dispatch( setFocusedCell( firstCell ));
        dispatch( setPrimaryRange({ anchorRow: rowIdx, anchorCol: 0, focusRow: rowIdx, focusCol: lastColIdx }));
      } else {
        // Keep the anchor's row, extend focus to the clicked row. Full-column
        // span is what makes BatchDelete recognize these rows as "fully selected".
        dispatch( setPrimaryRange({
          anchorRow: anchorRowIdx,
          anchorCol: 0,
          focusRow: rowIdx,
          focusCol: lastColIdx
        }));
        dispatch( setFocusedCell( firstCell ));
      }
    } else if ( e.ctrlKey || e.metaKey ) {
      // Ctrl+click row number: add an additional row-spanning range. The
      // range model can't subtract individual ranges, so this is add-only.
      dispatch( addAdditionalRange({
        anchorRow: rowIdx,
        anchorCol: 0,
        focusRow: rowIdx,
        focusCol: lastColIdx
      }));
      if ( !isRowSelected ) {
        dispatch( setFocusedCell( firstCell ));
      }
    } else {
      // Plain click row number: single-row span, replaces all prior selection.
      dispatch( setFocusedCell( firstCell ));
      dispatch( setPrimaryRange({
        anchorRow: rowIdx,
        anchorCol: 0,
        focusRow: rowIdx,
        focusCol: lastColIdx
      }));
    }
  };

  // Map the optional streaming status to a subtle whole-row tint class. Kept
  // separate from the cellChanges-derived `rowError`/`rowUnsaved` (which tint
  // only the row-number cell) so the two mechanisms never collide.
  const streamTintClass = streamStatus
    ? streamStatus.status === 'success'
      ? classes.rowStreamSuccess
      : streamStatus.status === 'failed'
        ? classes.rowStreamFailed
        : classes.rowStreamPending
    : '';

  const isErrored = streamStatus?.status === 'failed';
  const errorMessage = streamStatus?.error?.message;
  // Friendly label for the modal: the row's `title` cell when present (the
  // publications grid surfaces the publication title), else the row number.
  const rowLabel =
    typeof row.values.title === 'string' && row.values.title.trim()
      ? ( row.values.title as string )
      : `${t( 'row', 'Fila' )} ${index + 1}`;

  const rowNumberCell = (
    <div
      className={`${classes.rowNumberCell} ${isRowSelected ? classes.selectedRow : ''} ${rowStatus === 'error' ? classes.rowError : rowStatus === 'unsaved' ? classes.rowUnsaved : ''}`}
      data-row-id={row.id}
      onClick={handleRowNumberClick}
    >
      {index + 1}
      {isErrored ? (
        <Tooltip label={t( 'view_error_detail', 'Ver detalle del error' )} position="right" withArrow>
          <IconExclamationMark
            stroke={3}
            size={14}
            className={classes.errorIcon}
            data-testid={componentsTestIds.spreadsheetRowErrorIcon( row.id )}
            onClick={( e ) => {
              e.stopPropagation();
              setErrorModalOpen( true );
            }}
          />
        </Tooltip>
      ) : (
        <IconArrowsDiagonal stroke={1.5} size={14} className={classes.expandIcon} />
      )}
    </div>
  );

  return (
    <div
      ref={measureRef}
      data-index={virtualItemIndex}
      data-testid={componentsTestIds.spreadsheetRow( row.id )}
      className={`${classes.dataRow} ${streamTintClass}`}
      style={{
        position: 'absolute',
        top: `${start}px`,
        left: 0,
        // Sum of column widths — must exceed viewport so the sticky row-number
        // cell has slide range inside its containing block.
        width: 'max-content'
      }}
    >
      {rowNumberCell}
      {isErrored && (
        <Modal
          opened={errorModalOpen}
          onClose={() => setErrorModalOpen( false )}
          centered
          size={520}
          radius="lg"
          padding="xl"
          withCloseButton={false}
          overlayProps={{ backgroundOpacity: 0.55, blur: 4 }}
          classNames={{ content: classes.errorModalContent }}
        >
          <Stack gap="lg">
            <Group justify="space-between" align="flex-start" wrap="nowrap">
              <Group gap="sm" align="center" wrap="nowrap">
                <div className={classes.errorModalIcon}>
                  <IconAlertTriangle size={22} stroke={1.8} />
                </div>
                <div>
                  <Text fw={700} fz="md" lh={1.2}>
                    {t( 'row_save_error_title', 'No se pudo guardar' )}
                  </Text>
                  <Text fz="xs" c="dimmed" lineClamp={1}>{rowLabel}</Text>
                </div>
              </Group>
              <CloseButton onClick={() => setErrorModalOpen( false )} radius="md" />
            </Group>

            <div className={classes.errorModalBox}>
              <Text fz="sm" lh={1.5}>
                {errorMessage ?? t( 'row_save_error_unknown', 'Ocurrió un error al sincronizar con el canal.' )}
              </Text>
            </div>

            <Group justify="space-between" align="center" wrap="nowrap" gap="md">
              <Text fz="xs" c="dimmed" style={{ flex: 1, minWidth: 0 }}>
                {t( 'row_save_error_hint', 'El cambio no se aplicó. Corregilo y volvé a guardar.' )}
              </Text>
              <Button
                variant="light"
                color="red"
                radius="md"
                size="sm"
                style={{ flexShrink: 0 }}
                onClick={() => setErrorModalOpen( false )}
              >
                {t( 'understood', 'Entendido' )}
              </Button>
            </Group>
          </Stack>
        </Modal>
      )}
      {columns
        .filter(( col ) => !col.hidden )
        .map(( col, colIndex ) => {
          const facts = cellFactsByColumnId[col.id];
          return (
            <Cell
              key={col.id}
              rowId={row.id}
              columnId={col.id}
              value={row.values[col.id]}
              width={col.width}
              rowIndex={index}
              columnIndex={colIndex}
              cellType={col.type}
              // @ts-ignore FIXME: realmente solo son strings? revisar
              options={col.values ?? []}
              isReadonly={col.isReadonly}
              cutEdges={cutEdgesByColumnId ? cutEdgesByColumnId[col.id] ?? null : null}
              row={row}
              isSelected={facts?.isSelected ?? false}
              isFocused={facts?.isFocused ?? false}
              isEditing={facts?.isEditing ?? false}
              editingInitialValue={facts?.editingInitialValue}
              status={facts?.status ?? 'none'}
            />
          );
        })}
    </div>
  );
};

const areEqual = ( prevProps: DataRowProps, nextProps: DataRowProps ) => {
  if ( prevProps.row.id !== nextProps.row.id ) return false;
  if ( prevProps.index !== nextProps.index ) return false;
  if ( prevProps.row.values !== nextProps.row.values ) return false;
  if ( prevProps.isRowSelected !== nextProps.isRowSelected ) return false;
  if ( prevProps.columns !== nextProps.columns ) return false;
  if ( prevProps.virtualItemIndex !== nextProps.virtualItemIndex ) return false;
  if ( prevProps.start !== nextProps.start ) return false;
  return true;
};

export const DataRow = React.memo( DataRowComponent, areEqual );
DataRow.displayName = 'DataRow';
